import {
    ApiChatMessageSendStatus,
    ApiChatMessageKind,
    ApiChatReadState,
    type ChatDialogResponse,
    type ChatDialogMessageResponse,
} from '@/core/generated/api';

import { getChatDialogMessageText } from './salebotServiceEvent';
import type { ChatDialogReadBoundary } from './types';

/** Объединяет страницы списка без повторения диалога */
export const mergeChatDialogs = (
    current: readonly ChatDialogResponse[],
    page: readonly ChatDialogResponse[],
): ChatDialogResponse[] => {
    const dialogs = new Map(current.map((dialog) => [dialog.id, dialog]));
    page.forEach((dialog) => dialogs.set(dialog.id, dialog));

    return [...dialogs.values()];
};

/** Объединяет старую историю с показанной перепиской */
export const mergeChatDialogMessages = (
    older: readonly ChatDialogMessageResponse[],
    current: readonly ChatDialogMessageResponse[],
): ChatDialogMessageResponse[] => {
    const messages = new Map(older.map((message) => [message.id, message]));

    current.forEach((message) => {
        const previous = messages.get(message.id);

        const latePending = message.sendStatus?.id === ApiChatMessageSendStatus.Pending
            && previous?.sendStatus?.id !== ApiChatMessageSendStatus.Pending && previous?.sendStatus != null;

        messages.set(message.id, {
            ...message,
            ...(latePending
                ? {
                    sendStatus: previous.sendStatus,
                    retryAttemptId: previous.retryAttemptId,
                    sentAt: previous.sentAt
                }
                : {}),
            ...(previous?.readState?.id === ApiChatReadState.Read
                ? { readState: previous.readState }
                : {}),
        });
    });

    return [...messages.values()].sort((left, right) => (left.sequence ?? 0) - (right.sequence ?? 0));
};

/** Находит последнее сообщение, доступное для подтверждения прочтения */
export const getChatDialogReadBoundary = (
    messages: readonly ChatDialogMessageResponse[],
): ChatDialogReadBoundary | null => {
    const eligible = messages.filter((message) => (
        message.kind?.id !== ApiChatMessageKind.System
        && message.id && Number.isSafeInteger(message.sequence) && (message.sequence ?? 0) > 0
        && (message.sendStatus?.id === ApiChatMessageSendStatus.Received
            || message.sendStatus?.id === ApiChatMessageSendStatus.Accepted)
    ));

    const latest = eligible.reduce<ChatDialogMessageResponse | null>(
        (result, message) => (message.sequence ?? 0) > (result?.sequence ?? 0)
            ? message
            : result,
        null,
    );

    return latest?.id && latest.sequence
        ? {
            messageId: latest.id,
            sequence: latest.sequence
        }
        : null;
};

/** Находит входящее сообщение, которое оператор должен увидеть для подтверждения прочтения */
export const getChatDialogIncomingReadBoundary = (
    messages: readonly ChatDialogMessageResponse[],
): ChatDialogReadBoundary | null => getChatDialogReadBoundary(messages.filter((message) => !message.isOutgoing));

/** Отмечает входящие сообщения до подтверждённой границы прочтения */
export const markAcknowledgedChatDialogMessagesRead = (
    messages: readonly ChatDialogMessageResponse[],
    throughSequence: number,
): ChatDialogMessageResponse[] => messages.map((message) => {
    const sequence = message.sequence ?? 0;
    const unreadIncoming = !message.isOutgoing && message.readState?.id === ApiChatReadState.Unread;

    const confirmedMessage = message.sendStatus?.id === ApiChatMessageSendStatus.Received
        || message.sendStatus?.id === ApiChatMessageSendStatus.Accepted;

    const withinBoundary = Number.isSafeInteger(sequence) && sequence > 0 && sequence <= throughSequence;
    const acknowledged = unreadIncoming && confirmedMessage && withinBoundary;

    if (!acknowledged) return message;

    return {
        ...message,
        readState: {
            id: ApiChatReadState.Read,
            name: 'Прочитано'
        }
    };
});

/** Определяет наличие показанных непрочитанных входящих сообщений */
export const hasUnreadChatDialogMessages = (
    messages: readonly ChatDialogMessageResponse[]
): boolean => (
    messages.some((message) => !message.isOutgoing && message.readState?.id === ApiChatReadState.Unread)
);

/** Возвращает краткое содержимое последнего сообщения */
export const getChatDialogPreview = (message: ChatDialogMessageResponse | undefined): string => (
    (message
        ? getChatDialogMessageText(message).trim()
        : '')
    || ((message?.attachments?.length ?? 0) > 0
        ? 'Вложение'
        : 'Нет сообщений')
);


/** Добавляет к имени собеседника до четырёх кличек питомцев */
export const getChatDialogDisplayName = (dialog: ChatDialogResponse): string => {
    const name = dialog.companionName || 'Без имени';
    const pets = (dialog.companionPetNames ?? []).map((pet) => pet.trim()).filter(Boolean).slice(0, 4);

    return pets.length > 0
        ? `${name} (${pets.join(', ')})`
        : name;
};
